'use client';

import { token } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { UnverifiedAssetStatus } from '@terra/shared-contracts-assets';
import { Badge, Tooltip } from '../../';
import type { BadgeVariant } from '../../components/badge';
import {
  resolveUnverifiedAssetStatusLabelKey,
  resolveUnverifiedAssetStatusTooltipKey,
} from './utils/unverified-asset-status.utils';

type UnverifiedAssetStatusBadgeStyle = {
  variant: BadgeVariant;
  bg?: string;
  borderColor?: string;
};

const UNVERIFIED_ASSET_STATUS_STYLES: Record<
  UnverifiedAssetStatus,
  UnverifiedAssetStatusBadgeStyle
> = {
  FAILED: {
    variant: 'emphasis',
    bg: token('colors.bg.neutral.error'),
    borderColor: token('colors.border.neutral.error'),
  },
  BLOCKED: {
    variant: 'emphasis',
    bg: token('colors.bg.neutral.warning'),
    borderColor: token('colors.border.neutral.warning'),
  },
  REJECTED: { variant: 'outline' },
  PENDING: {
    variant: 'emphasis',
    bg: token('colors.bg.neutral.info'),
    borderColor: token('colors.border.neutral.info'),
  },
};

type UnverifiedAssetBadgeProps = {
  unverifiedStatus: UnverifiedAssetStatus;
  tone?: 'status' | 'neutral';
};

export function UnverifiedAssetBadge({
  unverifiedStatus,
  tone = 'status',
}: UnverifiedAssetBadgeProps) {
  const t = useTranslations('designSystem.applicationAssets');
  const labelKey = resolveUnverifiedAssetStatusLabelKey({ unverifiedStatus });
  const tooltipKey = resolveUnverifiedAssetStatusTooltipKey({
    unverifiedStatus,
  });
  const style =
    tone === 'neutral'
      ? { variant: 'emphasis' as const }
      : UNVERIFIED_ASSET_STATUS_STYLES[unverifiedStatus];
  const badge = (
    <Badge
      size="sm"
      variant={style.variant}
      bg={style.bg}
      borderColor={style.borderColor}
      flexShrink={0}
    >
      {t(labelKey)}
    </Badge>
  );

  if (!tooltipKey) {
    return badge;
  }

  return (
    <Tooltip title={t(tooltipKey)} maxW="320px">
      {badge}
    </Tooltip>
  );
}
